import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Terminal, Shield, Camera, Zap, Activity, RefreshCw,
  Play, Square, AlertTriangle, CheckCircle, XCircle,
  Server, Clock, User, Target, Cpu, Settings,
  ChevronDown, ChevronUp, Skull, Lock, Unlock,
  Radio, Eye, Download, Trash2, BarChart2, Search,
  Filter, Grid, List, MessageSquare, Sparkles, Bot,
  Code, Copy, ExternalLink, FileCode, Wand2, Brain,
  Rocket, Bug, Crosshair, Layers, BookOpen, Lightbulb,
  BadgeCheck, Loader, HelpCircle, Crown
} from 'lucide-react';
import PenetrationWorkflowDiagram from '../../components/PenetrationWorkflowDiagram';
import SessionsGuide, { SessionsWelcomeBanner, SessionsHelpButton, ContextualTooltip } from '../../components/SessionsGuide';
import { exploitSessions as exploitSessionsApi } from '../../services/api/client';
import { toast } from '../../utils/toastUtils';
import PlanRestrictedButton from '../../components/PlanRestrictedButton';
import PlanRestrictedPage from '../../components/PlanRestrictedPage';
import { usePlanRestrictions } from '../../hooks/usePlanRestrictions';
import LicenseBlock from '../../components/LicenseBlock';
import { useI18n } from '../../i18n';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

const SessionsList = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { hasFeature, currentPlan, isFreePlan } = usePlanRestrictions();
  const { workspaceId: activeWorkspaceId, activeWorkspace } = useWorkspace();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    stale: 0,
    closed: 0,
    persistent: 0,
    privileged: 0,
    hosts_compromised: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);

  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [selectedSessions, setSelectedSessions] = useState([]);

  // APT Module state
  const [aptModules, setAptModules] = useState([]);
  const [aptLoading, setAptLoading] = useState(false);
  const [selectedAptModule, setSelectedAptModule] = useState(null);
  const [aptExecutions, setAptExecutions] = useState({});
  const [aptSectionExpanded, setAptSectionExpanded] = useState(false);

  // Auto-exploit config
  const [autoExploitConfig, setAutoExploitConfig] = useState({
    enabled: false,
    auto_screenshot: true,
    auto_privesc: true,
    auto_persistence: false,
    max_concurrent: 5
  });
  const [showAutoExploitConfig, setShowAutoExploitConfig] = useState(false);

  // AI Assistant state
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);

  // PoC Auto state
  const [showPoCPanel, setShowPoCPanel] = useState(false);
  const [pocTargets, setPocTargets] = useState([]);
  const [pocLoading, setPocLoading] = useState(false);
  const [pocResults, setPocResults] = useState([]);
  const [selectedVulns, setSelectedVulns] = useState([]);

  // Quick actions
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Action states
  const [actionLoading, setActionLoading] = useState({});

  // Validation state
  const [isValidating, setIsValidating] = useState(false);

  // Guide/Tutorial state
  const [showGuide, setShowGuide] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  // Translate severity label
  const translateSeverity = (severity) => {
    if (!severity) return '-';
    const translated = t(`vulnerabilities.severities.${severity}`, null);
    if (translated && translated !== `vulnerabilities.severities.${severity}`) {
      return translated;
    }
    return severity.charAt(0).toUpperCase() + severity.slice(1);
  };

  // Check if token is valid before making API calls
  const hasValidToken = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (exp && Date.now() >= exp * 1000) {
        localStorage.removeItem('access_token');
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }, []);

  // Fetch all sessions
  const fetchSessions = useCallback(async () => {
    // Skip API call if no valid token - prevents errors during auth transitions
    if (!hasValidToken()) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Reset previous workspace data to avoid flash
      setSessions([]);
      setStats({});
      // Clear previous error on new fetch attempt (prevents stale error display)
      setError(null);

      let url = `${API_BASE}/api/v1/exploit/sessions`;
      const params = new URLSearchParams();
      if (activeWorkspaceId) {
        params.append('workspace_id', activeWorkspaceId);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      // Handle authentication errors silently by redirecting to login
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return;
      }

      if (!response.ok) {
        // Use French error messages for all HTTP error codes
        if (response.status === 500) {
          throw new Error(t('sessions.errors.serverError', 'Erreur serveur interne'));
        } else if (response.status === 503) {
          throw new Error(t('sessions.errors.serviceUnavailable', 'Service temporairement indisponible'));
        }
        throw new Error(t('sessions.errors.loadFailed', 'Load failed'));
      }

      const data = await response.json();
      setSessions(data.sessions || []);
      setStats(data.stats || {});
      // Check if backend returned an error in the response body
      if (data.error) {
        console.warn('Backend reported error:', data.error);
        // Don't show to user if sessions loaded - just log it
      }
      setError(null);
    } catch (err) {
      // Skip error display for auth-related errors
      if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return;
      }
      // Log internal error details for debugging, but show French message to user
      console.warn('Sessions fetch error:', err?.message);
      // Use the thrown error message if it's already translated, otherwise use generic message
      const errorMessage = err.message && !err.message.includes('fetch') && !err.message.includes('network')
        ? err.message
        : t('sessions.errors.connectionError', 'Erreur de connexion au serveur');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId, t, hasValidToken]);

  // Fetch APT modules
  const fetchAptModules = useCallback(async () => {
    // Skip API call if no valid token
    if (!hasValidToken()) return;
    try {
      setAptLoading(true);
      const response = await fetch(`${API_BASE}/api/v1/exploit/apt/modules`, {
        headers: getAuthHeaders(),
      });

      // Handle authentication errors silently
      if (response.status === 401) {
        return; // Let the main session fetch handle the redirect
      }

      if (response.ok) {
        const data = await response.json();
        setAptModules(data.modules || []);
      }
    } catch (err) {
      console.error('Failed to fetch APT modules:', err);
    } finally {
      setAptLoading(false);
    }
  }, [hasValidToken]);

  // Fetch auto-exploit config
  const fetchAutoExploitConfig = useCallback(async () => {
    // Skip API call if no valid token
    if (!hasValidToken()) return;

    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/auto-exploit/config`, {
        headers: getAuthHeaders(),
      });

      // Handle authentication errors silently
      if (response.status === 401) {
        return; // Let the main session fetch handle the redirect
      }

      if (response.ok) {
        const data = await response.json();
        setAutoExploitConfig(data.config || autoExploitConfig);
      }
    } catch (err) {
      console.error('Failed to fetch auto-exploit config:', err);
    }
  }, [hasValidToken]);

  // Fetch available vulnerabilities for PoC
  const fetchPoCTargets = useCallback(async () => {
    // Skip API call if no valid token
    if (!hasValidToken()) {
      setPocLoading(false);
      return;
    }

    try {
      setPocLoading(true);
      const response = await fetch(`${API_BASE}/api/v1/scans/vulnerabilities`, {
        headers: getAuthHeaders(),
      });

      // Handle authentication errors silently
      if (response.status === 401) {
        return; // Let the main session fetch handle the redirect
      }

      if (response.ok) {
        const data = await response.json();
        setPocTargets(data.vulnerabilities || []);
      }
    } catch (err) {
      console.error('Failed to fetch PoC targets:', err);
    } finally {
      setPocLoading(false);
    }
  }, [hasValidToken]);

  useEffect(() => {
    fetchSessions();
    fetchAptModules();
    fetchAutoExploitConfig();

    const interval = setInterval(fetchSessions, 10000);
    return () => clearInterval(interval);
  }, [fetchSessions, fetchAptModules, fetchAutoExploitConfig]);

  // AI Assistant query
  const askAI = async (query) => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/ai/analyze`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          query,
          context: {
            sessions: sessions.slice(0, 5),
            stats,
            type: 'exploitation_analysis'
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const newResponse = {
          query,
          response: data.response || data.analysis,
          suggestions: data.suggestions || [],
          timestamp: new Date().toISOString()
        };
        setAiResponse(newResponse);
        setAiHistory(prev => [newResponse, ...prev.slice(0, 9)]);
      } else {
        // Fallback local analysis
        const localAnalysis = generateLocalAnalysis(query);
        setAiResponse(localAnalysis);
        setAiHistory(prev => [localAnalysis, ...prev.slice(0, 9)]);
      }
    } catch (err) {
      console.error('AI query failed:', err);
      const localAnalysis = generateLocalAnalysis(query);
      setAiResponse(localAnalysis);
    } finally {
      setAiLoading(false);
      setAiQuery('');
    }
  };

  // Local AI analysis fallback - Enhanced with guidance
  const generateLocalAnalysis = (query) => {
    const activeSessions = sessions.filter(s => s.status === 'active');
    const privilegedCount = sessions.filter(s => s.privilege_escalated).length;
    const persistentCount = sessions.filter(s => s.persistence_installed).length;
    const queryLower = query.toLowerCase();

    let response = '';
    let suggestions = [];

    // Help / Getting started queries
    if (queryLower.includes('aide') || queryLower.includes('help') || queryLower.includes('comment') || queryLower.includes('quoi faire') || queryLower.includes('commencer')) {
      response = `Quick Start Guide:\n\n`;
      response += `Bienvenue dans les Sessions d'Exploitation! Voici comment utiliser cette page:\n\n`;
      response += `1. COMPRENDRE LES SESSIONS\n`;
      response += `   - A session = access to a compromised system\n`;
      response += `   - ACTIVE sessions (green) = established connection\n`;
      response += `   - CLOSED sessions (gray) = terminated connection\n\n`;
      response += `2. ACTIONS DISPONIBLES\n`;
      response += `   - Screenshot: Capture the target's screen\n`;
      response += `   - Privesc: Obtenir les droits administrateur\n`;
      response += `   - Persistence: Keep access after restart\n`;
      response += `   - Terminal: Execute commands\n\n`;
      response += `3. ADVANCED TOOLS\n`;
      response += `   - PoC Auto: Automatically exploit vulnerabilities\n`;
      response += `   - APT Modules: Advanced attack techniques\n`;
      response += `   - AI Assistant (me!): Personalized advice\n\n`;
      response += `Current status: ${stats.active || 0} active session(s), ${privilegedCount} privileged`;
      suggestions = ['What to do with an active session?', 'How to escalate privileges?', 'Explain persistence'];
    }
    // What to do with active sessions
    else if (queryLower.includes('session active') || queryLower.includes('quoi faire')) {
      if (activeSessions.length === 0) {
        response = `Vous n'avez pas de sessions actives actuellement.\n\n`;
        response += `To create a session:\n`;
        response += `1. Use the "PoC Auto" button to exploit vulnerabilities\n`;
        response += `2. Or launch a scan with exploitation enabled\n\n`;
        response += `Once the session is created, you can:\n`;
        response += `- Execute commands via terminal\n`;
        response += `- Capturer des screenshots\n`;
        response += `- Attempt privilege escalation\n`;
        response += `- Installer la persistence`;
      } else {
        response = `Vous avez ${activeSessions.length} session(s) active(s)!\n\n`;
        response += `Recommended actions:\n\n`;
        if (privilegedCount < activeSessions.length) {
          response += `1. PRIORITY: Privilege escalation\n`;
          response += `   ${activeSessions.length - privilegedCount} non-privileged session(s)\n`;
          response += `   Click the shield icon to attempt\n\n`;
        }
        if (persistentCount < activeSessions.length) {
          response += `2. Installer la persistence\n`;
          response += `   ${activeSessions.length - persistentCount} session(s) sans persistence\n`;
          response += `   Click the lock icon to install\n\n`;
        }
        response += `3. Collecter des informations\n`;
        response += `   - Screenshots pour documentation\n`;
        response += `   - System enumeration\n`;
        response += `   - Recherche de credentials`;
      }
      suggestions = ['How to escalate privileges?', 'Explain persistence', 'Scan internal network'];
    }
    // Privilege escalation
    else if (queryLower.includes('privesc') || queryLower.includes('privilege') || queryLower.includes('escalad') || queryLower.includes('root') || queryLower.includes('admin')) {
      response = `Privilege Escalation Guide:\n\n`;
      response += `CURRENT STATUS:\n`;
      response += `- Sessions actives: ${activeSessions.length}\n`;
      response += `- Already privileged: ${privilegedCount}\n`;
      response += `- To escalate: ${activeSessions.length - privilegedCount}\n\n`;
      response += `COMMENT FAIRE:\n`;
      response += `1. Click the shield icon (orange) next to the session\n`;
      response += `2. The system will attempt multiple techniques automatically\n\n`;
      response += `TECHNIQUES USED:\n`;
      response += `Linux: sudo, SUID, capabilities, kernel exploits, cron jobs\n`;
      response += `Windows: UAC bypass, misconfigured services, DLL hijacking\n\n`;
      response += `AFTER ESCALATION:\n`;
      response += `- "ROOT" badge appears on the session\n`;
      response += `- Plus de commandes disponibles\n`;
      response += `- Access to system files`;
      suggestions = ['Manual privesc techniques', 'Why did privesc fail?', 'Install persistence'];
    }
    // Persistence
    else if (queryLower.includes('persistence') || queryLower.includes('persistant') || queryLower.includes('garder')) {
      response = `Guide Persistence:\n\n`;
      response += `QU'EST-CE QUE LA PERSISTENCE?\n`;
      response += `Mechanism to maintain access even after:\n`;
      response += `- System restart\n`;
      response += `- User logout\n`;
      response += `- Network connection loss\n\n`;
      response += `CURRENT STATUS:\n`;
      response += `- Sessions avec persistence: ${persistentCount}\n`;
      response += `- Sessions sans persistence: ${activeSessions.length - persistentCount}\n\n`;
      response += `COMMENT INSTALLER:\n`;
      response += `1. Click the lock icon (purple) next to the session\n`;
      response += `2. An adapted method for the system will be chosen\n\n`;
      response += `METHODS USED:\n`;
      response += `Linux: cron, .bashrc, service systemd, SSH keys\n`;
      response += `Windows: Registry, Scheduled Tasks, Services, WMI`;
      suggestions = ['Verify if persistence works', 'Remove persistence', 'Lateral movement'];
    }
    // Lateral movement
    else if (queryLower.includes('lateral') || queryLower.includes('mouvement') || queryLower.includes('pivoter') || queryLower.includes('pivot')) {
      response = `Lateral Movement Guide:\n\n`;
      response += `OBJECTIF:\n`;
      response += `Move to other network machines from an existing access.\n\n`;
      response += `CURRENT STATUS:\n`;
      response += `- Compromised hosts: ${stats.hosts_compromised || 0}\n`;
      response += `- Privileged sessions (useful for pivoting): ${privilegedCount}\n\n`;
      response += `RECOMMENDED STEPS:\n`;
      response += `1. Enumerate the network from an active session\n`;
      response += `2. Identifier les autres machines accessibles\n`;
      response += `3. Collecter des credentials (hash, passwords)\n`;
      response += `4. Use these credentials to access other machines\n\n`;
      response += `TECHNIQUES:\n`;
      response += `- Pass-the-Hash (Windows AD)\n`;
      response += `- SSH with recovered credentials\n`;
      response += `- Exploitation de services internes\n`;
      response += `- PsExec, WMI, WinRM (Windows)`;
      suggestions = ['How to dump credentials?', 'Scan internal network', 'Pass-the-Hash explained'];
    }
    // APT modules
    else if (queryLower.includes('apt') || queryLower.includes('module') || queryLower.includes('avance')) {
      response = `Guide Modules APT:\n\n`;
      response += `QU'EST-CE QU'UN MODULE APT?\n`;
      response += `APT = Advanced Persistent Threat\n`;
      response += `These are sophisticated attack techniques mapped to MITRE ATT&CK.\n\n`;
      response += `COMMENT UTILISER:\n`;
      response += `1. Scroll to the "APT Modules" section\n`;
      response += `2. Click on a module to see its details\n`;
      response += `3. Les badges rouges montrent les techniques MITRE\n`;
      response += `4. Click "Execute APT" to launch\n\n`;
      response += `EXEMPLES DE MODULES:\n`;
      response += `- Reconnaissance: Complete system enumeration\n`;
      response += `- Credential Dumping: Extraction de mots de passe\n`;
      response += `- Lateral Movement: Network propagation\n`;
      response += `- Exfiltration: Data extraction`;
      suggestions = ['Which module to use first?', 'MITRE ATT&CK explained', 'Credential dumping'];
    }
    // PoC Auto
    else if (queryLower.includes('poc') || queryLower.includes('exploit') || queryLower.includes('vulnerability') || queryLower.includes('vuln')) {
      response = `Guide PoC Auto:\n\n`;
      response += `QU'EST-CE QUE PoC AUTO?\n`;
      response += `PoC = Proof of Concept\n`;
      response += `Automatically exploits vulnerabilities detected by your scans.\n\n`;
      response += `COMMENT UTILISER:\n`;
      response += `1. Cliquez sur le bouton "PoC Auto" en haut\n`;
      response += `2. Select vulnerabilities to exploit\n`;
      response += `3. Click "Execute PoC"\n`;
      response += `4. If successful, a new session is created!\n\n`;
      response += `CONSEILS:\n`;
      response += `- Prioritize "Critical" and "High" vulnerabilities\n`;
      response += `- You can select multiple vulnerabilities\n`;
      response += `- PoC code can be copied for manual use`;
      suggestions = ['No vulnerabilities available?', 'PoC failed, why?', 'Launch a new scan'];
    }
    // Default summary
    else {
      response = `Your sessions summary:\n\n`;
      response += `STATISTIQUES:\n`;
      response += `- Total sessions: ${stats.total || 0}\n`;
      response += `- Actives: ${stats.active || 0}\n`;
      response += `- Privileged: ${stats.privileged || 0}\n`;
      response += `- Persistantes: ${stats.persistent || 0}\n`;
      response += `- Compromised hosts: ${stats.hosts_compromised || 0}\n\n`;

      if (activeSessions.length === 0) {
        response += `RECOMMANDATION:\n`;
        response += `Vous n'avez pas de sessions actives.\n`;
        response += `Use "PoC Auto" to exploit vulnerabilities\n`;
        response += `or launch a scan with exploitation enabled.`;
        suggestions = ['How to create a session?', 'Use PoC Auto', 'Launch a scan'];
      } else {
        response += `NEXT RECOMMENDED STEPS:\n`;
        if (privilegedCount < activeSessions.length) {
          response += `1. Escalate privileges (${activeSessions.length - privilegedCount} session(s))\n`;
        }
        if (persistentCount < activeSessions.length) {
          response += `2. Installer persistence (${activeSessions.length - persistentCount} session(s))\n`;
        }
        response += `3. Collecter des informations sensibles\n`;
        response += `4. Explore lateral movement`;
        suggestions = ['How to escalate privileges?', 'Install persistence', 'Lateral movement'];
      }
    }

    return {
      query,
      response,
      suggestions,
      timestamp: new Date().toISOString(),
      isLocal: true
    };
  };

  // Auto PoC execution
  const executeAutoPoC = async (vulnIds) => {
    setPocLoading(true);
    setPocResults([]);

    for (const vulnId of vulnIds) {
      try {
        const response = await fetch(`${API_BASE}/api/v1/exploit/auto-poc`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            vulnerability_id: vulnId,
            safe_mode: true,
            generate_report: true
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setPocResults(prev => [...prev, {
            vuln_id: vulnId,
            success: data.success,
            poc_code: data.poc_code,
            exploit_path: data.exploit_path,
            session_id: data.session_id,
            message: data.message
          }]);
        }
      } catch (err) {
        setPocResults(prev => [...prev, {
          vuln_id: vulnId,
          success: false,
          error: err.message
        }]);
      }
    }

    setPocLoading(false);
    fetchSessions();
  };

  // Generate PoC code for vulnerability
  const generatePoCCode = async (vuln) => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/ai/generate-poc`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          vulnerability: vuln,
          language: 'python'
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.poc_code;
      }
    } catch (err) {
      console.error('PoC generation failed:', err);
    } finally {
      setAiLoading(false);
    }

    // Fallback template
    return generateLocalPoCTemplate(vuln);
  };

  // Local PoC template generator
  const generateLocalPoCTemplate = (vuln) => {
    const templates = {
      'sql_injection': `#!/usr/bin/env python3
# PoC: SQL Injection - ${vuln?.name || 'Unknown'}
# Target: ${vuln?.target || 'TARGET_URL'}

import requests

target = "${vuln?.target || 'http://target.com/vulnerable'}"
payload = "' OR '1'='1' --"

# Test injection
response = requests.get(f"{target}?id={payload}")
if "error" not in response.text.lower():
    print("[+] SQL Injection vulnerable!")
else:
    print("[-] Not vulnerable")`,

      'xss': `#!/usr/bin/env python3
# PoC: XSS - ${vuln?.name || 'Unknown'}
# Target: ${vuln?.target || 'TARGET_URL'}

payload = "<script>alert('XSS')</script>"
# Insert in vulnerable parameter

print(f"[*] Test payload: {payload}")
print("[*] Verify in browser for popup")`,

      'rce': `#!/usr/bin/env python3
# PoC: Remote Code Execution - ${vuln?.name || 'Unknown'}
# Target: ${vuln?.target || 'TARGET'}

import requests

target = "${vuln?.target || 'http://target.com'}"
cmd = "id"

# Exploit RCE
response = requests.post(target, data={"cmd": cmd})
print(f"[+] Response: {response.text}")`,

      'default': `#!/usr/bin/env python3
# PoC: ${vuln?.name || 'Vulnerability'}
# CVE: ${vuln?.cve || 'N/A'}
# Target: ${vuln?.target || 'TARGET'}

"""
Description: ${vuln?.description || 'Proof of Concept'}
Severity: ${vuln?.severity || 'Unknown'}

Steps:
1. Identify vulnerable endpoint
2. Craft exploit payload
3. Send request
4. Verify exploitation
"""

print("[*] Manual verification required")
print(f"[*] Target: ${vuln?.target || 'TARGET'}")
print(f"[*] Vulnerability: ${vuln?.name || 'Unknown'}")`
    };

    const vulnType = vuln?.type?.toLowerCase() || 'default';
    return templates[vulnType] || templates['default'];
  };

  // Take screenshot for a session
  const takeScreenshot = async (sessionId) => {
    setActionLoading(prev => ({ ...prev, [`screenshot_${sessionId}`]: true }));
    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/session/${sessionId}/screenshot`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(t('sessions.errors.screenshotFailed', 'Screenshot failed'));
      }

      const data = await response.json();
      toast.success(t('sessions.actions.screenshotSuccess', 'Screenshot captured'));
      fetchSessions();
    } catch (err) {
      console.error('Screenshot failed:', err);
      toast.error(t('sessions.errors.screenshotFailed', 'Screenshot failed'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`screenshot_${sessionId}`]: false }));
    }
  };

  // Privilege escalation
  const runPrivesc = async (sessionId) => {
    setActionLoading(prev => ({ ...prev, [`privesc_${sessionId}`]: true }));
    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/session/${sessionId}/privesc`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ method: 'auto' }),
      });

      if (!response.ok) {
        throw new Error(t('sessions.errors.privescFailed', 'Privilege escalation failed'));
      }

      const data = await response.json();
      if (data.success) {
        toast.success(t('sessions.actions.privescSuccess', 'Privilege escalation succeeded'));
      } else {
        toast.info(data.message || t('sessions.actions.privescCompleted', 'Escalation attempt completed'));
      }
      fetchSessions();
    } catch (err) {
      console.error('Privesc failed:', err);
      toast.error(t('sessions.errors.privescFailed', 'Privilege escalation failed'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`privesc_${sessionId}`]: false }));
    }
  };

  // Install persistence
  const installPersistence = async (sessionId) => {
    setActionLoading(prev => ({ ...prev, [`persistence_${sessionId}`]: true }));
    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/session/${sessionId}/persistence`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ method: 'auto' }),
      });

      if (!response.ok) {
        throw new Error(t('sessions.errors.persistenceFailed', 'Persistence installation failed'));
      }

      const data = await response.json();
      if (data.success) {
        toast.success(t('sessions.actions.persistenceSuccess', 'Persistence mechanism installed'));
      } else {
        toast.info(data.message || t('sessions.actions.persistenceCompleted', 'Installation complete'));
      }
      fetchSessions();
    } catch (err) {
      console.error('Persistence failed:', err);
      toast.error(t('sessions.errors.persistenceFailed', 'Persistence installation failed'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`persistence_${sessionId}`]: false }));
    }
  };

  // Execute APT module
  const executeAptModule = async (moduleId, targetScanId = null) => {
    setActionLoading(prev => ({ ...prev, [`apt_${moduleId}`]: true }));
    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/apt/execute/${moduleId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          scan_id: targetScanId,
          options: {}
        }),
      });

      if (!response.ok) {
        throw new Error(t('sessions.errors.aptExecutionFailed', 'APT module execution failed'));
      }

      const data = await response.json();
      setAptExecutions(prev => ({
        ...prev,
        [data.execution_id]: {
          id: data.execution_id,
          module: moduleId,
          status: 'running',
          started_at: new Date().toISOString()
        }
      }));

      pollAptStatus(data.execution_id);
    } catch (err) {
      console.error('APT execution failed:', err);
      toast.error(t('sessions.errors.aptExecutionFailed', 'APT module execution failed'));
    } finally {
      setActionLoading(prev => ({ ...prev, [`apt_${moduleId}`]: false }));
    }
  };

  // Poll APT execution status
  const pollAptStatus = async (executionId) => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/v1/exploit/apt/status/${executionId}`, {
          headers: getAuthHeaders(),
        });

        if (response.ok) {
          const data = await response.json();
          setAptExecutions(prev => ({
            ...prev,
            [executionId]: data
          }));

          if (data.status === 'running' || data.status === 'pending') {
            setTimeout(checkStatus, 3000);
          }
        }
      } catch (err) {
        console.error('APT status check failed:', err);
      }
    };

    checkStatus();
  };

  // Update auto-exploit config
  const updateAutoExploitConfig = async (newConfig) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/exploit/auto-exploit/config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(newConfig),
      });

      if (response.ok) {
        const data = await response.json();
        setAutoExploitConfig(data.config);
      }
    } catch (err) {
      console.error('Config update failed:', err);
    }
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    if (selectedSessions.length === 0) return;

    for (const sessionId of selectedSessions) {
      switch (action) {
        case 'screenshot':
          await takeScreenshot(sessionId);
          break;
        case 'privesc':
          await runPrivesc(sessionId);
          break;
        case 'persistence':
          await installPersistence(sessionId);
          break;
        default:
          break;
      }
    }
    setSelectedSessions([]);
  };

  // Validate sessions for report
  const handleValidateForReport = async () => {
    if (selectedSessions.length === 0) return;

    setIsValidating(true);
    try {
      const result = await exploitSessionsApi.validateForReport(selectedSessions, true);
      toast.success(`${result.validated_count} session(s) validated for reports`);
      setSelectedSessions([]);
    } catch (error) {
      console.error('Validation error:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsValidating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-brand-green/20 text-brand-green border-brand-green/50';
      case 'stale':
        return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'closed':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
      case 'error':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'idle':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      case 'unknown':
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };

  const getStatusLabel = (status) => {
    return t(`sessions.status.${status}`, t('sessions.status.unknown'));
  };

  const getSessionTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'meterpreter':
        return <Skull className="w-5 h-5" />;
      case 'shell':
        return <Terminal className="w-5 h-5" />;
      default:
        return <Server className="w-5 h-5" />;
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'critical':
        return 'text-red-500';
      case 'high':
        return 'text-orange-500';
      case 'medium':
        return 'text-yellow-500';
      default:
        return 'text-blue-500';
    }
  };

  // Format date with fallback for null values
  const formatSessionDate = (dateStr) => {
    if (!dateStr) return t('common.notAvailable');
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return t('common.notAvailable');
    return date.toLocaleString();
  };

  // Filter sessions
  const filteredSessions = sessions.filter(session => {
    const matchesSearch = !searchTerm ||
      session.target_ip?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.session_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.exploit_used?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || session.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // AI Quick prompts - Enhanced for guidance
  const aiQuickPrompts = [
    { icon: HelpCircle, label: t('sessions.quickPrompts.help'), query: 'Comment utiliser cette page? Aide-moi a commencer.' },
    { icon: Shield, label: t('sessions.quickPrompts.privesc'), query: 'How to escalate privileges on my sessions?' },
    { icon: Lock, label: t('sessions.quickPrompts.persistence'), query: 'Explain persistence to me and how to install it' },
    { icon: Layers, label: t('sessions.quickPrompts.lateral'), query: 'How to perform lateral movement to other machines?' },
    { icon: Rocket, label: t('sessions.quickPrompts.pocAuto'), query: 'How to use PoC Auto to exploit vulnerabilities?' },
    { icon: Bug, label: t('sessions.quickPrompts.aptModules'), query: 'Explain APT modules to me and how to use them' },
  ];

  return (
    <PlanRestrictedPage
      feature="exploitation_sessions"
      title={t('sessions.pageTitle', 'Sessions d\'exploitation')}
      description={t('sessions.pageDescription', 'Exploitation session management and compromised access')}
      icon={Skull}
    >
    <div className="min-h-screen bg-brand-black p-6">
      <div className="w-[95%] sm:w-[92%] lg:w-[88%] laptop:w-[85%] 2xl:w-[80%] max-w-[1600px] laptop:max-w-[1700px] 2xl:max-w-[1800px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gradient-green flex items-center gap-3">
              <Skull className="w-10 h-10 text-brand-green" />
              {t('sessions.pageTitle', 'Sessions d\'exploitation')}
            </h1>
            <p className="text-gray-400 mt-2">
              Manage your access to compromised systems - Hover over KPIs for more information
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowGuide(true)}
              className="px-4 py-2 rounded-lg flex items-center gap-2 transition-all bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 hover:from-blue-500/30 hover:to-purple-500/30 border border-blue-500/30 hover:border-blue-400/50 hover:scale-105"
              title="Guide complet et tutoriel interactif"
            >
              <HelpCircle className="w-4 h-4" />
              Aide & Guide
            </button>
            <LicenseBlock feature="les sessions d'exploitation">
            <PlanRestrictedButton
              feature="exploitation_sessions"
              onClick={() => {
                setShowPoCPanel(!showPoCPanel);
                if (!showPoCPanel) fetchPoCTargets();
              }}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap ${
                showPoCPanel
                  ? 'bg-brand-green text-black'
                  : 'bg-brand-green/20 text-brand-green hover:bg-brand-green/30'
              }`}
              disabledClassName="!bg-gray-700/50 !text-gray-500"
              tooltipPosition="bottom"
            >
              <Rocket className="w-4 h-4 flex-shrink-0" />
              PoC Auto
            </PlanRestrictedButton>
            </LicenseBlock>
            <LicenseBlock feature="les sessions d'exploitation">
            <PlanRestrictedButton
              feature="ai_assistant"
              onClick={() => setShowAIAssistant(!showAIAssistant)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showAIAssistant
                  ? 'bg-brand-green text-black'
                  : 'bg-brand-green/20 text-brand-green hover:bg-brand-green/30'
              }`}
              disabledClassName="!bg-gray-700/50 !text-gray-500"
              tooltipPosition="bottom"
            >
              <Brain className="w-4 h-4" />
              Assistant IA
            </PlanRestrictedButton>
            </LicenseBlock>
            <LicenseBlock feature="les sessions d'exploitation">
            <PlanRestrictedButton
              feature="exploitation_sessions"
              onClick={() => setShowAutoExploitConfig(!showAutoExploitConfig)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showAutoExploitConfig
                  ? 'bg-brand-green text-black'
                  : 'bg-brand-green/20 text-brand-green hover:bg-brand-green/30'
              }`}
              disabledClassName="!bg-gray-700/50 !text-gray-500"
              tooltipPosition="bottom"
            >
              <Settings className="w-4 h-4" />
              Auto-Exploit
            </PlanRestrictedButton>
            </LicenseBlock>
            <button
              onClick={fetchSessions}
              disabled={loading}
              className="px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Welcome Banner - How this page works */}
        {showWelcome && (
          <div className="bg-gradient-to-r from-brand-green/10 via-blue-500/10 to-purple-500/10 rounded-xl border border-brand-green/30 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-brand-green/20 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-brand-green" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">Comment utiliser cette page ?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-brand-black/50 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 rounded-full bg-brand-green text-black flex items-center justify-center text-sm font-bold">1</span>
                      <span className="font-semibold text-white">Select a Workspace</span>
                    </div>
                    <p className="text-sm text-gray-400">Choisissez le workspace client sur lequel vous travaillez dans le filtre ci-dessous.</p>
                  </div>
                  <div className="bg-brand-black/50 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">2</span>
                      <span className="font-semibold text-white">Lancez des Modules APT</span>
                    </div>
                    <p className="text-sm text-gray-400">Use APT modules to simulate advanced attacks on workspace targets.</p>
                  </div>
                  <div className="bg-brand-black/50 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-bold">3</span>
                      <span className="font-semibold text-white">Manage your Sessions</span>
                    </div>
                    <p className="text-sm text-gray-400">Created sessions will appear here. Take screenshots, escalate privileges.</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <button
                    onClick={() => setShowGuide(true)}
                    className="px-4 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/90 flex items-center gap-2"
                  >
                    <BookOpen className="w-4 h-4" />
                    Guide Complet
                  </button>
                  <button
                    onClick={() => setShowWelcome(false)}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                  >
                    Fermer ce guide
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 overflow-x-auto">
          {/* Total Sessions */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center">
                <Activity className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{stats.total || 0}</div>
                <div className="text-xs text-gray-500">{t('sessions.title')}</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-brand-green/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-brand-green" />
                <span className="font-semibold text-white">Sessions Totales</span>
              </div>
              <p className="text-xs text-gray-300">Total number of exploitation sessions created, including active and closed. A session represents access obtained on a target system after successful exploitation.</p>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                <div className="border-8 border-transparent border-t-brand-black"></div>
              </div>
            </div>
          </div>

          {/* Active Sessions */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-green-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <Radio className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-green-400">{stats.active || 0}</div>
                <div className="text-xs text-gray-500">Actives</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-green-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Radio className="w-4 h-4 text-green-400" />
                <span className="font-semibold text-white">Sessions Actives</span>
              </div>
              <p className="text-xs text-gray-300">Currently established connections to target systems. You can execute commands, capture screenshots and perform post-exploitation actions on these sessions.</p>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                <div className="border-8 border-transparent border-t-brand-black"></div>
              </div>
            </div>
          </div>

          {/* Stale Sessions */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-orange-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-400">{stats.stale || 0}</div>
                <div className="text-xs text-gray-500">To restart</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-orange-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <RefreshCw className="w-4 h-4 text-orange-400" />
                <span className="font-semibold text-white">Sessions to Restart</span>
              </div>
              <p className="text-xs text-gray-300">Sessions whose connection has expired or become inactive. If persistence was installed, you can try to restart the connection. Otherwise, you will need to re-exploit the vulnerability.</p>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                <div className="border-8 border-transparent border-t-brand-black"></div>
              </div>
            </div>
          </div>

          {/* Closed Sessions */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-gray-600/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-600/20 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-400">{stats.closed || 0}</div>
                <div className="text-xs text-gray-500">Closed</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-gray-600/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4 text-gray-400" />
                <span className="font-semibold text-white">Closed Sessions</span>
              </div>
              <p className="text-xs text-gray-300">Terminated or disconnected sessions. The connection is no longer active. If persistence was installed, you can try to reconnect via the persistence module.</p>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                <div className="border-8 border-transparent border-t-brand-black"></div>
              </div>
            </div>
          </div>

          {/* Persistent Sessions */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-purple-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-400">{stats.persistent || 0}</div>
                <div className="text-xs text-gray-500">Persistantes</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-purple-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-purple-400" />
                <span className="font-semibold text-white">Sessions Persistantes</span>
              </div>
              <p className="text-xs text-gray-300">Sessions with an installed persistence mechanism (cron, registry, service...). Access can be recovered even after target system restart or connection loss.</p>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                <div className="border-8 border-transparent border-t-brand-black"></div>
              </div>
            </div>
          </div>

          {/* Privileged Sessions */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-orange-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-400">{stats.privileged || 0}</div>
                <div className="text-xs text-gray-500">Privileged</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-orange-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-orange-400" />
                <span className="font-semibold text-white">Privileged Sessions</span>
              </div>
              <p className="text-xs text-gray-300">Sessions with root (Linux) or administrator (Windows) rights. These sessions allow complete system control: access to system files, configuration changes, service installation.</p>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                <div className="border-8 border-transparent border-t-brand-black"></div>
              </div>
            </div>
          </div>

          {/* Hosts Compromised */}
          <div className="bg-brand-black-light rounded-xl p-4 border border-red-500/30 relative group cursor-help">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400">{stats.hosts_compromised || 0}</div>
                <div className="text-xs text-gray-500">Compromised Hosts</div>
              </div>
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-brand-black border border-red-500/30 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white">Compromised Hosts</span>
              </div>
              <p className="text-xs text-gray-300">Number of unique machines on which access was obtained. The same host can have multiple sessions (e.g., different users or access methods).</p>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
                <div className="border-8 border-transparent border-t-brand-black"></div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Assistant Panel */}
        {showAIAssistant && (
          <div className="bg-brand-black-light rounded-xl border border-blue-500/30 overflow-hidden">
            <div className="p-4 border-b border-blue-500/20 bg-blue-500/10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-blue-400" />
                Assistant IA - Analyse d'Exploitation
                <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">LLM</span>
              </h3>
            </div>

            <div className="p-4 space-y-4">
              {/* Quick prompts */}
              <div className="flex flex-wrap gap-2">
                {aiQuickPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => askAI(prompt.query)}
                    disabled={aiLoading}
                    className="px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-colors flex items-center gap-2 text-sm"
                  >
                    <prompt.icon className="w-4 h-4" />
                    {prompt.label}
                  </button>
                ))}
              </div>

              {/* Custom query input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && aiQuery && askAI(aiQuery)}
                  placeholder="Posez une question sur l'exploitation, la post-exploitation, les techniques..."
                  className="flex-1 px-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <button
                  onClick={() => aiQuery && askAI(aiQuery)}
                  disabled={aiLoading || !aiQuery}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {aiLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Analyser
                </button>
              </div>

              {/* AI Response */}
              {aiResponse && (
                <div className="bg-brand-black rounded-lg p-4 border border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-400">AI response</span>
                        {aiResponse.isLocal && (
                          <span className="text-xs px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">Local</span>
                        )}
                        <span className="text-xs text-gray-600">
                          {new Date(aiResponse.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">
                        {aiResponse.response}
                      </pre>

                      {/* Suggestions */}
                      {aiResponse.suggestions && aiResponse.suggestions.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <div className="text-xs text-gray-500 mb-2">Suggested actions:</div>
                          <div className="flex flex-wrap gap-2">
                            {aiResponse.suggestions.map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={() => askAI(suggestion)}
                                className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs hover:bg-blue-500/20 transition-colors flex items-center gap-1"
                              >
                                <Lightbulb className="w-3 h-3" />
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* History */}
              {aiHistory.length > 1 && (
                <div className="border-t border-gray-700 pt-3">
                  <div className="text-xs text-gray-500 mb-2">Recent history:</div>
                  <div className="flex flex-wrap gap-2">
                    {aiHistory.slice(1, 5).map((item, i) => (
                      <button
                        key={i}
                        onClick={() => setAiResponse(item)}
                        className="px-2 py-1 bg-gray-800 text-gray-400 rounded text-xs hover:bg-gray-700 transition-colors truncate max-w-[200px]"
                      >
                        {item.query}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PoC Auto Panel */}
        {showPoCPanel && (
          <div className="bg-brand-black-light rounded-xl border border-purple-500/30 overflow-hidden">
            <div className="p-4 border-b border-purple-500/20 bg-purple-500/10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Rocket className="w-5 h-5 text-purple-400" />
                PoC Auto - Simplified Exploitation
                <span className="ml-2 text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">1-Click</span>
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Select vulnerabilities to exploit automatically
              </p>
            </div>

            <div className="p-4 space-y-4">
              {pocLoading && pocTargets.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
                </div>
              ) : pocTargets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bug className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('vulnerabilities.noVulnerabilities')}</p>
                  <p className="text-sm">{t('scans.newScan')}</p>
                </div>
              ) : (
                <>
                  {/* Vulnerability list */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[300px] overflow-y-auto">
                    {pocTargets.map((vuln, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          if (selectedVulns.includes(vuln.id)) {
                            setSelectedVulns(prev => prev.filter(v => v !== vuln.id));
                          } else {
                            setSelectedVulns(prev => [...prev, vuln.id]);
                          }
                        }}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedVulns.includes(vuln.id)
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-gray-700 bg-brand-black hover:border-purple-500/50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            vuln.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            vuln.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            vuln.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {translateSeverity(vuln.severity)}
                          </span>
                          <input
                            type="checkbox"
                            checked={selectedVulns.includes(vuln.id)}
                            onChange={() => {}}
                            className="w-4 h-4 rounded border-gray-600 bg-brand-black text-purple-500"
                          />
                        </div>
                        <h4 className="text-sm font-medium text-white truncate">{vuln.name}</h4>
                        <p className="text-xs text-gray-500 truncate">{vuln.target}</p>
                        {vuln.cve && (
                          <span className="text-xs text-purple-400">{vuln.cve}</span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                    <div className="text-sm text-gray-400">
                      {selectedVulns.length} vulnerability(ies) selected
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const vuln = pocTargets.find(v => selectedVulns.includes(v.id));
                          if (vuln) {
                            const code = generateLocalPoCTemplate(vuln);
                            navigator.clipboard.writeText(code);
                            alert('PoC code copied to clipboard!');
                          }
                        }}
                        disabled={selectedVulns.length === 0}
                        className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        <Code className="w-4 h-4" />
                        Generate Code
                      </button>
                      <button
                        onClick={() => executeAutoPoC(selectedVulns)}
                        disabled={selectedVulns.length === 0 || pocLoading}
                        className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {pocLoading ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                        Execute PoC
                      </button>
                    </div>
                  </div>

                  {/* PoC Results */}
                  {pocResults.length > 0 && (
                    <div className="space-y-2 pt-3 border-t border-gray-700">
                      <div className="text-sm text-gray-400">Results:</div>
                      {pocResults.map((result, i) => (
                        <div
                          key={i}
                          className={`p-3 rounded-lg border ${
                            result.success
                              ? 'border-green-500/50 bg-green-500/10'
                              : 'border-red-500/50 bg-red-500/10'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {result.success ? (
                              <CheckCircle className="w-4 h-4 text-green-400" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400" />
                            )}
                            <span className={result.success ? 'text-green-400' : 'text-red-400'}>
                              {result.success ? 'Exploitation successful!' : 'Failed'}
                            </span>
                            {result.session_id && (
                              <button
                                onClick={() => navigate(`/sessions/${result.session_id}`)}
                                className="ml-auto px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs hover:bg-green-500/30"
                              >
                                Voir Session
                              </button>
                            )}
                          </div>
                          {result.message && (
                            <p className="text-xs text-gray-400 mt-1">{result.message}</p>
                          )}
                          {result.poc_code && (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(result.poc_code);
                                alert('Code copied!');
                              }}
                              className="mt-2 px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs hover:bg-gray-600 flex items-center gap-1"
                            >
                              <Copy className="w-3 h-3" />
                              Copier PoC
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Auto-Exploit Configuration Panel */}
        {showAutoExploitConfig && (
          <div className="bg-brand-black-light rounded-xl p-6 border border-brand-green/30">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-brand-green" />
              Configuration Auto-Exploitation
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoExploitConfig.enabled}
                  onChange={(e) => updateAutoExploitConfig({ ...autoExploitConfig, enabled: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-600 bg-brand-black text-brand-green focus:ring-brand-green flex-shrink-0"
                />
                <span className="text-gray-300 whitespace-nowrap">Activer Auto-Exploit</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoExploitConfig.auto_screenshot}
                  onChange={(e) => updateAutoExploitConfig({ ...autoExploitConfig, auto_screenshot: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-600 bg-brand-black text-brand-green focus:ring-brand-green flex-shrink-0"
                />
                <span className="text-gray-300 whitespace-nowrap">Capture Auto</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoExploitConfig.auto_privesc}
                  onChange={(e) => updateAutoExploitConfig({ ...autoExploitConfig, auto_privesc: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-600 bg-brand-black text-brand-green focus:ring-brand-green flex-shrink-0"
                />
                <span className="text-gray-300 whitespace-nowrap">Escalade Auto</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoExploitConfig.auto_persistence}
                  onChange={(e) => updateAutoExploitConfig({ ...autoExploitConfig, auto_persistence: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-600 bg-brand-black text-brand-green focus:ring-brand-green flex-shrink-0"
                />
                <span className="text-gray-300 whitespace-nowrap">Persistance Auto</span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Max simultaneous:</span>
                <input
                  type="number"
                  value={autoExploitConfig.max_concurrent}
                  onChange={(e) => updateAutoExploitConfig({ ...autoExploitConfig, max_concurrent: parseInt(e.target.value) || 5 })}
                  className="w-16 px-2 py-1 bg-brand-black border border-gray-600 rounded text-white text-sm"
                  min="1"
                  max="20"
                />
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/20">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[280px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher par IP, type, module..."
                className="w-full pl-10 pr-4 py-2 bg-brand-black border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green placeholder:text-ellipsis placeholder:overflow-hidden"
              />
            </div>

            {/* Status filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actives</option>
                <option value="stale">To restart</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* View mode */}
            <div className="flex items-center gap-1 bg-brand-black rounded-lg p-1 border border-gray-700">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-brand-green text-black' : 'text-gray-400 hover:text-white'}`}
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-brand-green text-black' : 'text-gray-400 hover:text-white'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
            </div>

            {/* Quick actions */}
            <button
              onClick={() => setShowQuickActions(!showQuickActions)}
              className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showQuickActions
                  ? 'bg-brand-green text-black'
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
            >
              <Wand2 className="w-4 h-4" />
              {t('sessions.quickActions')}
            </button>
          </div>

          {/* Quick Actions Bar */}
          {showQuickActions && sessions.filter(s => s.status === 'active').length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-gray-400">Actions sur toutes les sessions actives:</span>
                <button
                  onClick={() => {
                    const activeSessions = sessions.filter(s => s.status === 'active').map(s => s.id);
                    setSelectedSessions(activeSessions);
                    handleBulkAction('screenshot');
                  }}
                  className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 flex items-center gap-2 text-sm"
                >
                  <Camera className="w-4 h-4" />
                  Screenshots
                </button>
                <button
                  onClick={() => {
                    const activeSessions = sessions.filter(s => s.status === 'active' && !s.privilege_escalated).map(s => s.id);
                    setSelectedSessions(activeSessions);
                    handleBulkAction('privesc');
                  }}
                  className="px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 flex items-center gap-2 text-sm"
                >
                  <Shield className="w-4 h-4" />
                  Elevate privileges
                </button>
                <button
                  onClick={() => {
                    const activeSessions = sessions.filter(s => s.status === 'active' && !s.persistence_installed).map(s => s.id);
                    setSelectedSessions(activeSessions);
                    handleBulkAction('persistence');
                  }}
                  className="px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 flex items-center gap-2 text-sm"
                >
                  <Lock className="w-4 h-4" />
                  Installer persistence
                </button>
                <button
                  onClick={() => {
                    const allSessionIds = sessions.map(s => s.id);
                    setSelectedSessions(allSessionIds);
                    handleValidateForReport();
                  }}
                  disabled={isValidating}
                  className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  {isValidating ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <BadgeCheck className="w-4 h-4" />
                  )}
                  {t('sessionsExtra.validateForReport')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* APT Modules Section - Collapsible */}
        <div className={`bg-brand-black-light rounded-xl border ${!activeWorkspaceId ? 'border-gray-700 opacity-60' : 'border-red-500/30'}`}>
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-brand-black/50 rounded-t-xl transition-colors"
            onClick={() => setAptSectionExpanded(!aptSectionExpanded)}
          >
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Skull className="w-5 h-5 text-red-500" />
              {t('sessionsExtra.aptModules')}
              <span className="text-sm font-normal text-gray-400">
                {aptModules.length} {t('sessionsExtra.modulesAvailable')}
              </span>
            </h3>
            <div className="flex items-center gap-3">
              {activeWorkspaceId && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-400">{t('nav.workspaces')}:</span>
                  <span className="px-2 py-1 bg-brand-green/20 text-brand-green rounded font-medium">
                    {activeWorkspace?.name || activeWorkspaceId}
                  </span>
                </div>
              )}
              {aptSectionExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {/* APT Section Content - Only show when expanded */}
          {aptSectionExpanded && (
            <div className="px-6 pb-6">
              {/* Warning if no workspace selected - hide when there's an API error to avoid multiple error states */}
              {!activeWorkspaceId && !error ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-8 h-8 text-yellow-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">Select a Workspace</h4>
                  <p className="text-gray-400 max-w-md mx-auto mb-4">
                    To use APT modules, you must first select a client workspace via the left menu.
                    Attacks will be executed on this workspace's targets.
                  </p>
                </div>
              ) : !activeWorkspaceId && error ? (
                /* When there's an error, show a minimal placeholder instead of the full warning */
                <div className="text-center py-4 text-gray-500 text-sm">
                  Modules APT indisponibles
                </div>
              ) : aptLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 text-red-400 animate-spin" />
                </div>
              ) : aptModules.length > 0 ? (
                <div className="space-y-6">
              {/* Group modules by category */}
              {Object.entries(
                aptModules.reduce((acc, module) => {
                  const category = module.category || 'other';
                  if (!acc[category]) acc[category] = [];
                  acc[category].push(module);
                  return acc;
                }, {})
              ).map(([category, modules]) => (
                <div key={category}>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      category === 'reconnaissance' ? 'bg-blue-500' :
                      category === 'initial_access' ? 'bg-orange-500' :
                      category === 'execution' ? 'bg-pink-500' :
                      category === 'persistence' ? 'bg-yellow-500' :
                      category === 'privilege_escalation' ? 'bg-red-500' :
                      category === 'defense_evasion' ? 'bg-purple-500' :
                      category === 'credential_access' ? 'bg-indigo-500' :
                      category === 'discovery' ? 'bg-cyan-500' :
                      category === 'lateral_movement' ? 'bg-teal-500' :
                      category === 'collection' ? 'bg-green-500' :
                      category === 'command_and_control' ? 'bg-lime-500' :
                      category === 'exfiltration' ? 'bg-amber-500' :
                      category === 'impact' ? 'bg-red-600' :
                      category === 'full_chain' ? 'bg-red-500' :
                      category === 'apt_simulation' ? 'bg-red-700' :
                      'bg-gray-500'
                    }`}></span>
                    {category.replace(/_/g, ' ')}
                    <span className="text-xs text-gray-500">({modules.length})</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {modules.map((module) => (
                      <div
                        key={module.id}
                        className={`p-3 rounded-lg border transition-all cursor-pointer ${
                          selectedAptModule === module.id
                            ? 'border-red-500 bg-red-500/10'
                            : 'border-gray-700 bg-brand-black hover:border-red-500/50'
                        }`}
                        onClick={() => setSelectedAptModule(selectedAptModule === module.id ? null : module.id)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-semibold text-white text-sm leading-tight">{module.name}</h5>
                          <span className={`text-xs px-1.5 py-0.5 rounded ml-2 whitespace-nowrap ${getRiskLevelColor(module.risk_level)}`}>
                            {module.risk_level}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mb-2">{module.description}</p>

                        {/* Duration estimate */}
                        {module.duration_estimate && (
                          <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {module.duration_estimate}
                          </div>
                        )}

                        {/* MITRE Techniques */}
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(module.techniques || module.mitre_techniques)?.slice(0, 3).map((tech, i) => (
                            <span key={i} className="text-xs px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">
                              {tech}
                            </span>
                          ))}
                          {(module.techniques || module.mitre_techniques)?.length > 3 && (
                            <span className="text-xs px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded">
                              +{(module.techniques || module.mitre_techniques).length - 3}
                            </span>
                          )}
                        </div>

                        {/* Execution button */}
                        {selectedAptModule === module.id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              executeAptModule(module.id);
                            }}
                            disabled={actionLoading[`apt_${module.id}`]}
                            className="w-full mt-2 px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                          >
                            {actionLoading[`apt_${module.id}`] ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Play className="w-3 h-3" />
                            )}
                            Execute
                          </button>
                        )}

                        {/* Execution status */}
                        {Object.values(aptExecutions).filter(e => e.module === module.id).map((exec) => (
                          <div key={exec.id} className="mt-2 p-2 bg-brand-black rounded border border-gray-700">
                            <div className="flex items-center justify-between text-xs">
                              <span className={`flex items-center gap-1 ${
                                exec.status === 'completed' ? 'text-green-400' :
                                exec.status === 'running' ? 'text-yellow-400' :
                                exec.status === 'failed' ? 'text-red-400' : 'text-gray-400'
                              }`}>
                                {exec.status === 'running' && <RefreshCw className="w-3 h-3 animate-spin" />}
                                {getStatusLabel(exec.status)}
                              </span>
                              <span className="text-gray-500">{exec.id?.slice(0, 8)}</span>
                            </div>
                            {exec.progress && (
                              <div className="mt-1">
                                <div className="w-full h-1 bg-gray-700 rounded overflow-hidden">
                                  <div
                                    className="h-full bg-red-500 transition-all"
                                    style={{ width: `${exec.progress}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p className="text-sm">{t('noModulesAvailable', 'Aucune campagne APT disponible pour le moment')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <div className="text-red-400 font-medium">{t('sessions.errors.loadingError')}</div>
              <div className="text-sm text-gray-400">{error}</div>
            </div>
            <button
              onClick={fetchSessions}
              className="ml-auto px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {t('errors.retry')}
            </button>
          </div>
        )}

        {/* Bulk actions bar */}
        {selectedSessions.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-brand-black-light border border-brand-green/50 rounded-xl px-6 py-3 flex items-center gap-4 shadow-2xl z-50">
            <span className="text-white font-medium">{selectedSessions.length} session(s)</span>
            <div className="h-6 w-px bg-gray-700" />
            <button
              onClick={() => handleBulkAction('screenshot')}
              className="px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 flex items-center gap-2"
            >
              <Camera className="w-4 h-4" />
              Captures
            </button>
            <button
              onClick={() => handleBulkAction('privesc')}
              className="px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-lg hover:bg-orange-500/30 flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              Privileges
            </button>
            <button
              onClick={() => handleBulkAction('persistence')}
              className="px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 flex items-center gap-2"
            >
              <Lock className="w-4 h-4" />
              Persistence
            </button>
            <button
              onClick={() => setSelectedSessions([])}
              className="px-3 py-1.5 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Annuler
            </button>
          </div>
        )}

        {/* Sessions List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-brand-green animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Chargement des sessions...</p>
            </div>
          </div>
        ) : error ? (
          /* Don't show empty state when there's an error - error banner above is sufficient */
          null
        ) : filteredSessions.length > 0 ? (
          viewMode === 'list' ? (
            <div className="space-y-4">
              {filteredSessions.map((session) => (
                <div
                  key={session.id}
                  className={`bg-brand-black-light rounded-xl border transition-all ${
                    session.status === 'active'
                      ? 'border-brand-green/30 hover:border-brand-green/50'
                      : session.status === 'stale'
                      ? 'border-orange-500/30 hover:border-orange-500/50'
                      : 'border-gray-700 hover:border-gray-600'
                  } ${selectedSessions.includes(session.id) ? 'ring-2 ring-brand-green' : ''}`}
                >
                  {/* Session Header */}
                  <div
                    className="p-5 cursor-pointer"
                    onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <input
                          type="checkbox"
                          checked={selectedSessions.includes(session.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (selectedSessions.includes(session.id)) {
                              setSelectedSessions(prev => prev.filter(id => id !== session.id));
                            } else {
                              setSelectedSessions(prev => [...prev, session.id]);
                            }
                          }}
                          className="w-5 h-5 rounded border-gray-600 bg-brand-black text-brand-green"
                        />
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          session.status === 'active' ? 'bg-brand-green/20'
                          : session.status === 'stale' ? 'bg-orange-500/20'
                          : 'bg-gray-700'
                        }`}>
                          {getSessionTypeIcon(session.session_type)}
                        </div>

                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <span className={`px-2 py-0.5 text-xs rounded border ${getStatusColor(session.status)}`}>
                              {getStatusLabel(session.status)}
                            </span>
                            <span className="text-white font-bold">
                              {session.session_type || t('sessions.defaultSessionType')} {t('sessions.sessionLabel')}
                            </span>
                            {session.privilege_escalated && (
                              <span className="px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded border border-orange-500/50">
                                {t('sessions.badges.root')}
                              </span>
                            )}
                            {session.persistence_installed && (
                              <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded border border-purple-500/50">
                                {t('sessions.badges.persistent')}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {session.target_ip || session.host?.ip || t('common.notAvailable')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatSessionDate(session.opened_at || session.created_at)}
                            </span>
                            {session.msf_session_id && (
                              <span className="flex items-center gap-1">
                                <Cpu className="w-3 h-3" />
                                MSF #{session.msf_session_id}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {session.status === 'active' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                takeScreenshot(session.id);
                              }}
                              disabled={actionLoading[`screenshot_${session.id}`]}
                              className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                              title="Screenshot"
                            >
                              {actionLoading[`screenshot_${session.id}`] ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Camera className="w-4 h-4" />
                              )}
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                runPrivesc(session.id);
                              }}
                              disabled={actionLoading[`privesc_${session.id}`] || session.privilege_escalated}
                              className={`p-2 rounded-lg transition-colors ${
                                session.privilege_escalated
                                  ? 'bg-orange-500/20 text-orange-400 cursor-not-allowed'
                                  : 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30'
                              }`}
                              title="Privilege escalation"
                            >
                              {actionLoading[`privesc_${session.id}`] ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Shield className="w-4 h-4" />
                              )}
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                installPersistence(session.id);
                              }}
                              disabled={actionLoading[`persistence_${session.id}`] || session.persistence_installed}
                              className={`p-2 rounded-lg transition-colors ${
                                session.persistence_installed
                                  ? 'bg-purple-500/20 text-purple-400 cursor-not-allowed'
                                  : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
                              }`}
                              title="Installer la persistance"
                            >
                              {actionLoading[`persistence_${session.id}`] ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Lock className="w-4 h-4" />
                              )}
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/sessions/${session.id}`);
                              }}
                              className="p-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors"
                              title="Interagir"
                            >
                              <Terminal className="w-4 h-4" />
                            </button>
                          </>
                        )}

                        {expandedSession === session.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Session Details */}
                  {expandedSession === session.id && (
                    <div className="px-5 pb-5 border-t border-gray-700">
                      <div className="pt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Session Info */}
                        <div className="bg-brand-black rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">Session ID</div>
                          <div className="text-sm text-white font-mono truncate">{session.id}</div>
                        </div>

                        <div className="bg-brand-black rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">{t('sessions.platform')}</div>
                          {session.platform || session.session_metadata?.platform ? (
                            <div className="text-sm text-white">{session.platform || session.session_metadata?.platform}</div>
                          ) : (
                            <div className="text-sm text-gray-500 italic">{t('sessions.platformUnknown')}</div>
                          )}
                        </div>

                        <div className="bg-brand-black rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">{t('sessions.exploitUsed')}</div>
                          <div className="text-sm text-white truncate">{session.exploit_used || t('common.notAvailable')}</div>
                        </div>

                        <div className="bg-brand-black rounded-lg p-3">
                          <div className="text-xs text-gray-500 mb-1">{t('sessions.commandsExecuted')}</div>
                          <div className="text-sm text-white">{session.commands_executed?.length || 0}</div>
                        </div>

                        {/* AI Quick Analysis */}
                        <div className="col-span-2 md:col-span-4 bg-blue-500/5 rounded-lg p-3 border border-blue-500/20">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs text-blue-400 flex items-center gap-1">
                              <Brain className="w-3 h-3" />
                              Analyse IA rapide
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowAIAssistant(true);
                                askAI(`Analyze session ${session.id} on ${session.target_ip}: type=${session.session_type}, privileged=${session.privilege_escalated}. What actions do you recommend?`);
                              }}
                              className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30"
                            >
                              Analyser
                            </button>
                          </div>
                          <p className="text-xs text-gray-400">
                            {session.privilege_escalated
                              ? "Privileged session - Credential collection and lateral movement recommended"
                              : "Non-privileged session - Privilege escalation attempt recommended"}
                          </p>
                        </div>

                        {/* Screenshots */}
                        {session.screenshots && session.screenshots.length > 0 && (
                          <div className="col-span-2 md:col-span-4 bg-brand-black rounded-lg p-3">
                            <div className="text-xs text-gray-500 mb-2">Screenshots ({session.screenshots.length})</div>
                            <div className="flex gap-2 overflow-x-auto">
                              {session.screenshots.slice(0, 5).map((screenshot, i) => (
                                <div key={i} className="flex-shrink-0 w-32 h-20 bg-gray-800 rounded overflow-hidden">
                                  <img
                                    src={`${API_BASE}/api/v1/exploit/screenshots/${screenshot?.filename || screenshot}`}
                                    alt={`Screenshot ${i + 1}`}
                                    className="w-full h-full object-cover"
                                    onError={(e) => e.target.style.display = 'none'}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recent Commands */}
                        {session.commands_executed && session.commands_executed.length > 0 && (
                          <div className="col-span-2 md:col-span-4 bg-brand-black rounded-lg p-3">
                            <div className="text-xs text-gray-500 mb-2">Recent Commands</div>
                            <div className="space-y-1 font-mono text-xs max-h-32 overflow-y-auto">
                              {session.commands_executed.slice(-5).map((cmd, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  <span className="text-brand-green">$</span>
                                  <span className="text-gray-300">{cmd?.command || cmd}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredSessions.map((session) => (
                <div
                  key={session.id}
                  className={`bg-brand-black-light rounded-xl border p-4 transition-all cursor-pointer ${
                    session.status === 'active'
                      ? 'border-brand-green/30 hover:border-brand-green/50'
                      : session.status === 'stale'
                      ? 'border-orange-500/30 hover:border-orange-500/50'
                      : 'border-gray-700 hover:border-gray-600'
                  } ${selectedSessions.includes(session.id) ? 'ring-2 ring-brand-green' : ''}`}
                  onClick={() => navigate(`/sessions/${session.id}`)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      session.status === 'active' ? 'bg-brand-green/20'
                      : session.status === 'stale' ? 'bg-orange-500/20'
                      : 'bg-gray-700'
                    }`}>
                      {getSessionTypeIcon(session.session_type)}
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded border ${getStatusColor(session.status)}`}>
                      {getStatusLabel(session.status)}
                    </span>
                  </div>

                  <h4 className="text-white font-bold mb-1">{session.session_type || t('sessions.defaultSessionType')}</h4>
                  <p className="text-sm text-gray-400 mb-3">{session.target_ip || t('common.notAvailable')}</p>

                  <div className="flex flex-wrap gap-1 mb-3">
                    {session.privilege_escalated && (
                      <span className="px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded border border-orange-500/50">{t('sessions.badges.root')}</span>
                    )}
                    {session.persistence_installed && (
                      <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded border border-purple-500/50">{t('sessions.badges.persistent')}</span>
                    )}
                  </div>

                  <div className="text-xs text-gray-500">
                    {formatSessionDate(session.opened_at || session.created_at)}
                  </div>

                  {session.status === 'active' && (
                    <div className="mt-3 pt-3 border-t border-gray-700 flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          takeScreenshot(session.id);
                        }}
                        className="flex-1 p-2 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30"
                      >
                        <Camera className="w-4 h-4 mx-auto" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          runPrivesc(session.id);
                        }}
                        disabled={session.privilege_escalated}
                        className="flex-1 p-2 bg-orange-500/20 text-orange-400 rounded hover:bg-orange-500/30 disabled:opacity-50"
                      >
                        <Shield className="w-4 h-4 mx-auto" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          installPersistence(session.id);
                        }}
                        disabled={session.persistence_installed}
                        className="flex-1 p-2 bg-purple-500/20 text-purple-400 rounded hover:bg-purple-500/30 disabled:opacity-50"
                      >
                        <Lock className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : sessions.length > 0 ? (
          /* Sessions exist but filtered out */
          <div className="bg-brand-black-light rounded-xl p-12 border border-gray-700 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-700/50 flex items-center justify-center">
              <Search className="w-10 h-10 text-gray-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-300 mb-2">{t('scans.noResults')}</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              {t('sessions.noSessions')}
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
              className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex items-center gap-2 mx-auto"
            >
              <Filter className="w-4 h-4" />
              {t('common.reset')}
            </button>
          </div>
        ) : (
          /* No sessions at all */
          <div className="bg-brand-black-light rounded-xl p-12 border border-brand-green/20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-brand-green/10 flex items-center justify-center">
              <Terminal className="w-10 h-10 text-brand-green" />
            </div>
            <h3 className="text-2xl font-bold text-gray-300 mb-2">{t('sessions.noSessions')}</h3>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              {t('sessions.pageDescription')}
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => navigate('/scans/new')}
                className="px-4 py-2 bg-brand-green text-black rounded-lg hover:bg-brand-green/90 flex items-center gap-2"
              >
                <Crosshair className="w-4 h-4" />
                {t('scans.newScan')}
              </button>
              <button
                onClick={() => {
                  setShowPoCPanel(true);
                  fetchPoCTargets();
                }}
                className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 flex items-center gap-2"
              >
                <Rocket className="w-4 h-4" />
                PoC Auto
              </button>
            </div>
          </div>
        )}

        {/* Workflow Diagram */}
        <div className="mt-8">
          <PenetrationWorkflowDiagram />
        </div>
      </div>

      {/* Guide/Tutorial Modal */}
      <SessionsGuide
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        onStartTutorial={() => setShowGuide(true)}
      />

      {/* Floating Help Button */}
      <SessionsHelpButton onClick={() => setShowGuide(true)} />
    </div>
    </PlanRestrictedPage>
  );
};

export default SessionsList;
